"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { handleGenerateBrandUrl } from "@faire/retailer-visitor-shared/components/SearchProducts/utils";
import { ProductTileContext } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/common";
import {
  SearchProductTileBrandLinkProps,
  ProductClickForSearchPageParameters,
} from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/components/BrandInfo/Links/SearchProductTileBrandLinkProps";
import { useGetProductTileClickImpressions } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ComposableProductTile/__internal__/__internal__/productTileActions/useGetProductTileClickImpressions";
import { MarketplaceProductBrandLink } from "@faire/retailer-visitor-shared/features/MarketplaceItems/MarketplaceProductBrandLink";
import { useSettingBrandLinkHoverThreshold } from "@faire/retailer-visitor-shared/settings/getSettingBrandLinkHoverTheshold";
import { getSettingRscLibp } from "@faire/retailer-visitor-shared/settings/getSettingRscLibp";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { trackSeoSearchProductListBrandLinkRefsClick } from "@faire/web-api--source/events/seo/click/searchProductListBrandLinkRefs";
import kebabCase from "lodash/kebabCase";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React, { useCallback, useContext, useMemo, useRef } from "react";
import { trackProductTileBrandLinkHover } from "@faire/web-api--source/events/productTile/hover/brandLink";

/**
 * Used for brand info on search page rsc version.
 * Non rsc link: use MarketplaceProductBrandLinkBase instead
 */
export const SearchProductTileBrandLink: React.FC<{
  data: SearchProductTileBrandLinkProps;
  children: React.ReactNode;
}> = ({ data, children }) => {
  const PREFETCH_DELAY_MS = useSettingBrandLinkHoverThreshold();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const { prefetch: nextPrefetch } = useNextRouterContext();
  const isRSCBrandPage = getSettingRscLibp();

  const trackingParams = data.trackingParams;
  const { brandToken: contextBrandToken } = useContext(ProductTileContext);

  const generateBrandUrl = useMemo(
    () =>
      handleGenerateBrandUrl(data.productSearchString, {
        productToken: trackingParams.token,
        brandToken: data.brandToken ?? contextBrandToken,
        impressionId: trackingParams.impressionId,
      }),
    [
      data.productSearchString,
      trackingParams.token,
      trackingParams.impressionId,
      data.brandToken,
      contextBrandToken,
    ]
  );
  const onBrandClickSearchPage = useOnBrandClickSearchPage(
    trackingParams,
    data.brandToken,
    data.productSearchString
  );
  const onBrandClick = useCallback(
    (ev: React.MouseEvent<HTMLAnchorElement>) => {
      onBrandClickSearchPage.onBrandClick(ev);
      clearTimeout(timeoutRef.current);
    },
    [onBrandClickSearchPage]
  );
  const brandUrl = generateBrandUrl();

  const onBrandLinkFocus = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      if (isRSCBrandPage) {
        nextPrefetch(brandUrl, {
          kind: PrefetchKind.FULL,
        });
      }
    }, PREFETCH_DELAY_MS);
  }, [brandUrl, nextPrefetch, isRSCBrandPage, PREFETCH_DELAY_MS]);
  const onBrandLinkBlur = useCallback(() => {
    clearTimeout(timeoutRef.current);
  }, []);

  return (
    <MarketplaceProductBrandLink
      token={trackingParams.token}
      onClick={(ev) => {
        onBrandClick?.(ev);
      }}
      onMouseEnter={() => {
        onBrandLinkFocus?.();
        trackProductTileBrandLinkHover();
      }}
      onMouseLeave={() => {
        onBrandLinkBlur?.();
      }}
      onFocus={() => {
        onBrandLinkFocus?.();
      }}
      onBlur={() => {
        onBrandLinkBlur?.();
      }}
      onTouchStart={() => {
        onBrandLinkFocus?.();
        trackProductTileBrandLinkHover();
      }}
      data-test-id="marketplace-product-brand-link"
      to={brandUrl}
    >
      {children}
    </MarketplaceProductBrandLink>
  );
};

const useOnBrandClickSearchPage = (
  params: ProductClickForSearchPageParameters,
  brandToken: string | undefined,
  productSearchString: string
) => {
  const { recordBrandNameClickEvent } =
    useGetProductTileClickImpressions(params);

  return {
    onBrandClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      recordBrandNameClickEvent();

      const productToken = params.token;
      if (!productSearchString || !productToken || !brandToken) {
        return;
      }
      trackEvent(
        `/search-click/${kebabCase(
          productSearchString
        )}/product/${productToken}/brand/${brandToken}`
      );
      trackSeoSearchProductListBrandLinkRefsClick(
        brandToken ?? "",
        productSearchString
      );

      event.stopPropagation();
    },
  };
};
